/**
 * GTM click bridge — makes the legacy {@code data-click-event} GTM contract fire
 * on pages where the clicked element is isolated from GTM's native click
 * listener.
 *
 * <p>The germanos GTM container ({@code GTM-NFJJC9}) fires its GA4 link tags from
 * per-event "Click - Just Links" triggers keyed on the clicked link's
 * {@code data-click-event} (e.g. {@code Click_Banner} is trigger
 * {@code 1688912_320}), also reading {@code data-section} / {@code data-position} /
 * {@code title} / {@code href}. That native listener runs on the top
 * {@code document}, so it works for LIGHT-DOM pages (homepage + React routes) but
 * is blind to two isolated contexts:
 * <ul>
 *   <li><b>Shadow-DOM CMS pages</b> — a click inside a shadow tree is retargeted
 *       at the shadow boundary, so GTM sees the shadow host {@code <div>}, never
 *       the inner {@code <a data-click-event>}.</li>
 *   <li><b>Iframe CMS pages</b> — the page's scripts run in a separate browsing
 *       context whose {@code dataLayer} GTM never reads.</li>
 * </ul>
 *
 * <p>This bridge reproduces the EXACT manual push the legacy ATG site used when
 * its own listener couldn't reach an element (see {@code touch.js} /
 * {@code cosmoteId-login.js} / {@code topBar.jsp} in {@code as-is/legacy}):
 * {@code event: 'gtm.linkClick'} + an (empty) {@code gtm.triggers} + the flat
 * {@code gtm.element.dataset.*} / {@code gtm.element.title} / {@code gtm.elementUrl}
 * keys the GA4 tags read. The empty {@code gtm.triggers} satisfies every click
 * trigger's {@code ^$} filter branch, leaving {@code data-click-event} as the sole
 * discriminator (see {@link GTM_TRIGGERS_EMPTY}). We are reproducing the legacy
 * integration as-is, not inventing a new analytics contract.
 *
 * <p><b>No double counting:</b> the bridge only fires for elements whose root is
 * a {@link ShadowRoot} (or that arrive via the iframe {@code postMessage}). For
 * light-DOM elements GTM's native listener already fires, so the bridge skips
 * them.
 */

import { isTrustedCmsIframeMessageOrigin } from './postMessageOrigin';

/** postMessage type emitted by the iframe bridge script for a tracked click. */
export const CMS_GTM_CLICK_MESSAGE = 'germanos-cms-gtm-click';

/**
 * postMessage type emitted by the iframe bridge script for an in-content
 * service-nav {@code Click_Menu_Item} click (see {@link pushClickMenuItem}).
 * Separate from {@link CMS_GTM_CLICK_MESSAGE} because service-nav items use the
 * legacy event-name push pattern, not the {@code data-click-event} contract.
 */
export const CMS_MENU_ITEM_MESSAGE = 'germanos-cms-menu-item';

/**
 * The {@code gtm.triggers} value stamped on the manual push.
 *
 * <p>Every germanos click trigger's tag carries the GTM-generated firing filter
 * {@code {{gtm.triggers}} matches RegEx (^$|((^|,)<id>($|,)))} — i.e. it fires
 * when {@code gtm.triggers} is EMPTY <b>or</b> contains that trigger's own id
 * (verified in GTM Preview: the "Click Banner" tag is trigger {@code 1688912_320}).
 * Because the {@code ^$} (empty) branch is present on all of them, an EMPTY value
 * satisfies EVERY click trigger, and the real discriminator is the
 * {@code data-click-event} filter. So we deliberately send empty here rather than
 * a specific id — that makes the bridge trigger-agnostic (works for Click_Banner,
 * Click_Promo_Slider, Click_Quick_Nav, … and any future value) instead of firing
 * only the one tag bound to a hardcoded id. It is also reset explicitly (not
 * omitted) so a stale value from a previous native click can't linger on the
 * shared dataLayer and mis-match a trigger.
 */
const GTM_TRIGGERS_EMPTY = '';

/** The legacy {@code data-*} fields a tracked click carries into the dataLayer. */
export interface TrackedClick {
  /** Legacy {@code data-click-event}, e.g. {@code "Click_Banner"} — the GA4 event name driver. */
  clickEvent: string;
  /** Legacy {@code data-section}. */
  section?: string;
  /** Legacy {@code data-position}. */
  position?: string;
  /** The element's {@code title} attribute. */
  title?: string;
  /** The element's resolved URL ({@code href}). */
  url?: string;
  /**
   * The element's FULL {@code data-*} set as camelCased key → value pairs
   * (e.g. {@code data-product-id} → {@code {productId: '…'}}). Forwarded by the
   * iframe bridge because a live {@code gtm.element} can't cross the document
   * boundary; each entry is stamped as {@code gtm.element.dataset.<key>}.
   */
  dataset?: Record<string, string>;
  /** The element's {@code id}, stamped as {@code gtm.element.id} when present. */
  id?: string;
}

/**
 * Pushes the legacy-compatible {@code gtm.linkClick} event to the top-window
 * dataLayer. Optionally pins {@code gtm.element} to the real node so any GTM
 * element-attribute variable resolves THIS element (shadow-DOM case).
 *
 * @param data the tracked click's legacy {@code data-*} fields
 * @param element the real element to pin as {@code gtm.element} (omitted for
 *        iframe-forwarded clicks, whose element lives in another document)
 */
function pushGtmLinkClick(data: TrackedClick, element?: HTMLElement): void {
  if (typeof window === 'undefined' || !data.clickEvent) {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  const payload: Record<string, unknown> = {
    event: 'gtm.linkClick',
    'gtm.triggers': GTM_TRIGGERS_EMPTY,
    'gtm.element.dataset.clickEvent': data.clickEvent,
    'gtm.element.dataset.section': data.section ?? '',
    'gtm.element.dataset.position': data.position ?? '',
    'gtm.element.title': data.title ?? '',
    'gtm.elementUrl': data.url ?? '',
  };
  // Iframe-forwarded clicks carry the element's data-* set as VALUES (no live node
  // can cross the document boundary to be pinned as gtm.element), so stamp every
  // one as gtm.element.dataset.<key> — this is how GA4 tags reading extra
  // attributes (productId, color, orderId, …) resolve on iframe pages. Duplicating
  // the explicit clickEvent/section/position keys above is harmless (same values).
  if (data.dataset) {
    for (const [key, value] of Object.entries(data.dataset)) {
      payload[`gtm.element.dataset.${key}`] = value;
    }
  }
  if (data.id) {
    payload['gtm.element.id'] = data.id;
  }
  if (element) {
    payload['gtm.element'] = element;
  }
  w.dataLayer.push(payload);
}

/** The fields an iframe-forwarded {@code Click_Menu_Item} click carries. */
interface MenuItemClick {
  /** The item's label — legacy {@code data-section} (tag reads it as {@code title}). */
  section?: string;
  /** Menu item type — legacy {@code data-type} (service-nav items use {@code "Corporate"}). */
  menuType?: string;
  /** Nested menu path — legacy {@code data-site-tree-path}. */
  siteTreePath?: string;
  /** The item's resolved URL ({@code href}). */
  url?: string;
}

/**
 * Pushes the legacy {@code Click_Menu_Item} GA4 event for an in-content
 * service-nav item clicked on an IFRAME CMS page.
 *
 * <p>The shadow path stamps {@code data-*} on the link and pins the live node as
 * {@code gtm.element} (see {@code shadowRenderer.ts} +
 * {@code dataLayerPushClickMenuItem} in {@code lib/ga4.ts}, which pushes
 * {@code {event:'Click_Menu_Item', 'gtm.element': link}}). A cross-document
 * {@code postMessage} can't carry that node, so we forward the attribute VALUES
 * the "GA4 Menu Item" tag reads — {@code data-section} → title,
 * {@code data-type} → menu_item_type, {@code data-site-tree-path} →
 * site_tree_path (see {@code buildMenuItemAttrs} in {@code lib/ga4.ts}) — stamped
 * as the same {@code gtm.element.dataset.*} keys the rest of this bridge uses.
 * No-op during SSR.
 *
 * @param data the menu item's forwarded label / type / path / url
 */
function pushClickMenuItem(data: MenuItemClick): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: 'Click_Menu_Item',
    'gtm.element.dataset.section': data.section ?? '',
    'gtm.element.dataset.type': data.menuType ?? '',
    'gtm.element.dataset.siteTreePath': data.siteTreePath ?? '',
    'gtm.elementUrl': data.url ?? '',
  });
}

/**
 * Reads the legacy {@code data-*} fields off an element carrying
 * {@code data-click-event}.
 *
 * @param el the element to read
 * @return the tracked-click payload
 */
function readTrackedClick(el: HTMLElement): TrackedClick {
  const anchor = el as HTMLAnchorElement;
  const href = typeof anchor.href === 'string' && anchor.href
    ? anchor.href
    : (el.getAttribute('href') ?? '');
  return {
    clickEvent: el.dataset.clickEvent ?? '',
    section: el.dataset.section,
    position: el.dataset.position,
    title: el.getAttribute('title') ?? '',
    url: href,
  };
}

/**
 * Finds the nearest {@code [data-click-event]} element in a composed event path
 * (crosses open shadow boundaries, unlike {@code Element.closest} from the
 * retargeted document-level target).
 *
 * @param path the {@code event.composedPath()} array
 * @return the first {@code [data-click-event]} element, or {@code null}
 */
function findTrackedElement(path: EventTarget[]): HTMLElement | null {
  for (const node of path) {
    if (node instanceof HTMLElement && node.dataset && node.dataset.clickEvent) {
      return node;
    }
  }
  return null;
}

/**
 * Returns true when a {@code message} event's source is the {@code contentWindow}
 * of one of our own CMS iframes ({@code <iframe class="cms-page-content">}, the
 * only iframe {@link ../pages/cms-page/CmsPageContent} renders).
 *
 * <p>This replaces a brittle {@code event.origin === location.origin} check: a
 * {@code srcDoc} iframe is {@code about:srcdoc}, whose {@code location.origin}
 * serializes to the string {@code "null"}, so an origin-equality gate always
 * rejects its messages. Scoping to our known iframe's {@code contentWindow}
 * (plus the message type/shape check in the listener) keeps acceptance safe
 * without depending on the unusable srcDoc origin.
 *
 * @param source the {@code MessageEvent.source}
 * @return true if {@code source} is a CMS iframe's {@code contentWindow}
 */
function isCmsIframeSource(source: MessageEventSource | null): boolean {
  if (!source || typeof document === 'undefined') {
    return false;
  }
  const iframes = document.querySelectorAll('iframe.cms-page-content');
  for (const iframe of Array.from(iframes)) {
    if ((iframe as HTMLIFrameElement).contentWindow === (source as unknown as Window)) {
      return true;
    }
  }
  return false;
}

let installed = false;

/**
 * Installs the GTM click bridge once per session. Safe to call repeatedly
 * (subsequent calls are no-ops) and during SSR (no-op without {@code window}).
 */
export function installGtmClickBridge(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  if (installed) {
    return;
  }
  installed = true;

  // Shadow-DOM CMS pages: a top-document capture listener sees every click, and
  // composedPath() exposes the real (shadowed) target. We push only when the
  // element is inside a shadow root — light-DOM clicks are already handled by
  // GTM's native listener, so pushing here would double count.
  document.addEventListener(
    'click',
    (event: Event) => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const el = findTrackedElement(path);
      if (!el) {
        return;
      }
      const root = el.getRootNode();
      const inShadow = root instanceof ShadowRoot;
      if (!inShadow) {
        // Light DOM → GTM's native listener already fires this; pushing would double count.
        return;
      }
      pushGtmLinkClick(readTrackedClick(el), el);
    },
    true,
  );

  // Iframe CMS pages: the injected iframe bridge (see cmsLinkNavigation.ts)
  // posts the click payload up here; replay it into the top-window dataLayer.
  window.addEventListener('message', (event: MessageEvent) => {
    if (!isTrustedCmsIframeMessageOrigin(event.origin)) {
      return;
    }
    const data = event.data as (TrackedClick & { type?: string }) | null;
    if (!data || data.type !== CMS_GTM_CLICK_MESSAGE || !data.clickEvent) {
      return;
    }
    // srcDoc iframes report origin "null", so origin-equality to location.origin
    // always fails. Accept only "null" or our own origin (see helper) and only
    // when the source is our CMS iframe's contentWindow.
    if (!isCmsIframeSource(event.source)) {
      return;
    }
    pushGtmLinkClick({
      clickEvent: data.clickEvent,
      section: data.section,
      position: data.position,
      title: data.title,
      url: data.url,
      dataset: data.dataset,
      id: data.id,
    });
  });

  // Iframe CMS pages: in-content service-nav items post a Click_Menu_Item message
  // (the shadowRenderer workaround that handles this for shadow pages never runs
  // inside an iframe). Replay it into the top-window dataLayer. Same source gate.
  window.addEventListener('message', (event: MessageEvent) => {
    if (!isTrustedCmsIframeMessageOrigin(event.origin)) {
      return;
    }
    const data = event.data as (MenuItemClick & { type?: string }) | null;
    if (!data || data.type !== CMS_MENU_ITEM_MESSAGE) {
      return;
    }
    if (!isCmsIframeSource(event.source)) {
      return;
    }
    pushClickMenuItem({
      section: data.section,
      menuType: data.menuType,
      siteTreePath: data.siteTreePath,
      url: data.url,
    });
  });
}
